import React, { useEffect } from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';

import DatePickerCustom from '../../../components/DatePicker/DatePickerCustom';
import { getWingsUnderDeptId } from '../../../redux/features/generalSlice';
import { ogdDepartmentId } from '../../../utils/constants/departmentDetails';
import { ogdWings } from '../../../utils/constants/GlobalStaticData';

/**
 * The month and squad filter for the OGD Point Tracker.
 *
 * A COPY OF MonthlyWiseGlobalFilter, ON PURPOSE.
 *
 * That component is shared by four other Point Tracker screens, Overview, Team
 * Wise, Sogo Milestone and one that is commented out. Turning its single Team
 * dropdown into a tick list would change all of them at once, which is not what
 * anybody asked for. So this screen has its own, and the shared one is left
 * exactly as it is.
 *
 * WHY A TICK LIST AND NOT ONE TEAM AT A TIME
 *
 * The table has to open showing the five selling squads together, the way the
 * department's own sheet does. A single dropdown can show one squad or everything,
 * and everything would drag in wing 67, Manager, and its 45 people. Ticking says
 * exactly which squads are being looked at, and it is written on the screen rather
 * than hidden in the code.
 *
 * The five selling squads are ticked when the page opens. Any other OGD squad can
 * be ticked on for a single look, and unticking back to the five is one click on
 * Reset filter.
 */
/**
 * The size the two date pickers use, so the Teams box can be given the same.
 *
 * DatePickerCustom is handed height and fontSize and puts both on the
 * OutlinedInput root. Anything else here has to match those numbers or the row
 * stops lining up.
 */
const FIELD_HEIGHT = 36;
const FIELD_FONT = '0.75rem';

const SellingWingFilter = ({
  fromDate,
  toDate,
  setFromDate,
  setToDate,
  selectedWings,
  setSelectedWings,
  // the switch that hides everybody with no target for the month
  onlyWithTarget,
  setOnlyWithTarget,
  withTargetCount = 0,
  totalCount = 0,
  // the selling squads, as the server named them. Not written down here on
  // purpose: one list, in one place, is the only way the two cannot disagree.
  defaultWings = [],
}) => {
  const dispatch = useDispatch();
  const wingsUnderDeptId = useSelector((state) => state.general.wingsUnderDeptId);
  const { data: wingsData } = wingsUnderDeptId;

  useEffect(() => {
    if (wingsData?.length === 0) {
      dispatch(getWingsUnderDeptId(ogdDepartmentId));
    }
  }, [wingsData]);

  // the live list where we have it, the hard coded one until it arrives, so the
  // filter is never empty while the page is still loading
  // the id is forced to a number. The constant holds numbers, and an id that
  // arrived from the API as text would never match it, so every tick box would
  // sit there unticked with nothing obviously wrong.
  const wings = wingsData?.length
    ? wingsData.map((wing) => ({ id: Number(wing.wing_id), value: wing.wing_name }))
    : ogdWings;

  // the selling squads first, so the ones normally ticked are at the top of the
  // list instead of scattered through it
  const ordered = [
    ...wings.filter((w) => defaultWings.includes(w.id)),
    ...wings.filter((w) => !defaultWings.includes(w.id)),
  ];

  const nameOf = (id) => wings.find((w) => w.id === id)?.value || `Wing ${id}`;

  /**
   * What the closed box says. One line, whatever is ticked.
   *
   * The usual case is every selling squad, and naming all five would only spill
   * off the end of the box, so it says so in words instead. Anything else is
   * named, and a long list trails off with dots rather than pushing the box out
   * of line with the dates.
   */
  const summary = (picked) => {
    const sameAsDefault =
      defaultWings.length > 0 &&
      picked.length === defaultWings.length &&
      [...picked].sort().join() === [...defaultWings].sort().join();

    const text = sameAsDefault
      ? `All selling squads (${picked.length})`
      : picked.map(nameOf).join(', ');

    return (
      <Typography noWrap sx={{ fontSize: FIELD_FONT, color: 'text.primary' }}>
        {text}
      </Typography>
    );
  };

  /**
   * At least one squad must stay ticked.
   *
   * Unticking the last one would send an empty list, and an empty list means "use
   * the default five" on the server. The table would then quietly show something
   * different from what the tick boxes say, so the last tick simply will not come
   * off.
   */
  const handleChange = (event) => {
    const picked = event.target.value;
    if (!picked.length) return;
    setSelectedWings(picked);
  };

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        borderRadius: '10px',
        p: { xs: 0, md: 1.5 },
      }}
    >
      <Grid container spacing={{ xs: 1, md: 2 }}>
        <Grid item xs={12} md={3}>
          <DatePickerCustom
            label="From Date"
            value={dayjs(fromDate)}
            setValue={(date) => setFromDate(date.format('YYYY-MM-DD'))}
            viewDetails={['year', 'month', 'day']}
            fullWidth
            height={36}
            fontSize="0.75rem"
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <DatePickerCustom
            label="To Date"
            value={dayjs(toDate)}
            setValue={(date) => setToDate(date.format('YYYY-MM-DD'))}
            viewDetails={['year', 'month', 'day']}
            fullWidth
            height={36}
            fontSize="0.75rem"
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel id="ogd-teams-label" sx={{ fontSize: FIELD_FONT }}>
              Teams
            </InputLabel>
            <Select
              labelId="ogd-teams-label"
              multiple
              value={selectedWings}
              onChange={handleChange}
              // The OutlinedInput carries no styling of its own. Anything put in
              // its sx is thrown away, because Select applies its OWN sx to the
              // same element and the second one wins. That is what left this box
              // 23px tall next to two 36px date pickers.
              input={<OutlinedInput label="Teams" />}
              // so everything goes here instead, on the Select, in one place.
              // The height and the font are the same numbers DatePickerCustom is
              // handed, so the three fields line up exactly.
              sx={{
                height: FIELD_HEIGHT,
                fontSize: FIELD_FONT,
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: FIELD_FONT,
                  // MUI gives the inner box a minimum height of its own, which
                  // would push the field back out past 36px
                  minHeight: 'unset !important',
                  height: '100%',
                  paddingTop: 0,
                  paddingBottom: 0,
                },
              }}
              // ONE LINE, always. Chips looked better but they wrap, and a box
              // that grows a second row stops lining up with the dates the moment
              // more than two squads are ticked. A summary never changes height.
              renderValue={summary}
              MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
            >
              {ordered.map((wing) => (
                <MenuItem key={wing.id} value={wing.id} sx={{ py: 0.25 }}>
                  <Checkbox
                    size="small"
                    checked={selectedWings.includes(wing.id)}
                    sx={{ p: 0.5 }}
                  />
                  <ListItemText
                    primary={wing.value}
                    primaryTypographyProps={{ fontSize: '0.8rem' }}
                    // a squad that is not one of the selling ones says so, so
                    // nobody wonders why the numbers suddenly look different
                    secondary={
                      defaultWings.includes(wing.id) ? 'Selling squad' : 'Not on the sheet'
                    }
                    secondaryTypographyProps={{ fontSize: '0.65rem' }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={3}>
          <Tooltip
            title={
              'A target is set by the squad head between the 1st and the 5th. ' +
              'Turning this on hides everybody who has no target for the month, ' +
              'which is normally new joiners still being settled in. ' +
              'Turning it off shows the whole squad again.'
            }
          >
            <FormControlLabel
              sx={{ ml: 0, height: FIELD_HEIGHT }}
              control={
                <Switch
                  size="small"
                  checked={Boolean(onlyWithTarget)}
                  onChange={(event) => setOnlyWithTarget(event.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontSize: FIELD_FONT, lineHeight: 1.2 }}>
                    Only with a target
                  </Typography>
                  {/* the two numbers, so the switch says what it will do before
                      anybody flips it and wonders where the rows went */}
                  <Typography
                    sx={{ fontSize: '0.68rem', lineHeight: 1.2 }}
                    color="text.secondary"
                  >
                    {withTargetCount} of {totalCount} have one
                  </Typography>
                </Box>
              }
            />
          </Tooltip>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SellingWingFilter;
